import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate, todayISO, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Scale, Plus, RefreshCw, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <Scale size={11} /> Reconciliation
      </div>
      <h1 className="font-black text-3xl tracking-tight">Daily Tally</h1>
      <p className="text-sm text-gray-500 mt-1">Physical stock count vs system stock — apply adjustments automatically</p>
    </div>
  );
}

export function DailyTallyPage() {
  const { auth } = useAuth();
  const isAdmin  = auth?.user?.role === "admin";
  const [godowns, setGodowns] = useState([]);
  const [tallies, setTallies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Active tally session
  const [tallyDate,   setTallyDate]   = useState(todayISO());
  const [tallyGodown, setTallyGodown] = useState("");
  const [suggestion,  setSuggestion]  = useState(null);
  const [loadingSug,  setLoadingSug]  = useState(false);
  const [tallyRows,   setTallyRows]   = useState([]); // [{brand,size,ground_filled,ground_mt,system_filled,system_mt}]
  const [note,        setNote]        = useState("");
  const [savingTally, setSavingTally] = useState(false);
  const [applyingAdj, setApplyingAdj] = useState("");
  const [expanded,    setExpanded]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/tally"),
        api.get("/stock"),
      ]);
      setTallies(t.data || []);
      // Extract godowns from live stock (stock returns {godown: [...items]})
      const gSet = new Set(Object.keys(s.data || {}));
      setGodowns([...gSet].sort());
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSuggestion = async () => {
    if (!tallyDate || !tallyGodown) { setError("Select date and godown first"); return; }
    setLoadingSug(true); setError("");
    try {
      const r = await api.get(`/tally/suggest?date=${tallyDate}&godown=${encodeURIComponent(tallyGodown)}`);
      setSuggestion(r.data);
      setTallyRows((r.data?.rows || []).map(row => ({
        brand: row.brand, size: row.size,
        ground_filled: row.ground_filled,
        ground_mt: row.ground_mt,
        system_filled: row.system_filled,
        system_mt: row.system_mt,
      })));
      setNote(r.data?.existing_note || "");
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoadingSug(false); }
  };

  const updateRow = (brand, size, field, value) => {
    setTallyRows(prev => prev.map(r =>
      r.brand === brand && r.size === size ? { ...r, [field]: parseInt(value) || 0 } : r
    ));
  };

  const saveTally = async () => {
    setSavingTally(true);
    try {
      await api.post("/tally", { date: tallyDate, godown: tallyGodown, note, rows: tallyRows });
      setSuggestion(null); setTallyRows([]); setNote("");
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSavingTally(false); }
  };

  const applyAdj = async (tid) => {
    if (!window.confirm("Apply adjustments to live stock? This will create adjustment entries.")) return;
    setApplyingAdj(tid);
    try {
      const r = await api.post(`/tally/${tid}/apply-adjustment`);
      if (r.data?.message) { setError(r.data.message); }
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setApplyingAdj(""); }
  };

  const mismatches = tallyRows.filter(r =>
    r.ground_filled !== r.system_filled || r.ground_mt !== r.system_mt
  );

  return (
    <div className="px-6 py-6 max-w-5xl" data-testid="daily-tally-page">
      <PageHeader />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* New Tally panel */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <h2 className="font-bold text-sm text-gray-800 mb-4 uppercase tracking-wide">Start New Tally</h2>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input type="date" value={tallyDate} onChange={e => setTallyDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="tally-date" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Godown *</label>
            <select value={tallyGodown} onChange={e => setTallyGodown(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 min-w-[160px]" data-testid="tally-godown">
              <option value="">Select godown</option>
              {godowns.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <button onClick={loadSuggestion} disabled={loadingSug || !tallyGodown}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-50"
            data-testid="load-tally-btn">
            <RefreshCw size={14} className={loadingSug ? "animate-spin" : ""} />
            {loadingSug ? "Loading…" : "Load Stock"}
          </button>
        </div>

        {/* Tally grid */}
        {tallyRows.length > 0 && (
          <>
            {mismatches.length > 0 && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                <AlertTriangle size={14} />
                {mismatches.length} mismatch{mismatches.length > 1 ? "es" : ""} found
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border border-gray-200 mb-3">
              <table className="w-full text-sm" data-testid="tally-grid">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Brand</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Size</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">System Filled</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-blue-600">Ground Filled</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">System MT</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-blue-600">Ground MT</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500">Diff F / MT</th>
                  </tr>
                </thead>
                <tbody>
                  {tallyRows.map(row => {
                    const df = row.ground_filled - row.system_filled;
                    const dm = row.ground_mt - row.system_mt;
                    const hasDiff = df !== 0 || dm !== 0;
                    return (
                      <tr key={`${row.brand}-${row.size}`} className={`border-b border-gray-100 ${hasDiff ? "bg-amber-50/30" : ""}`}>
                        <td className="px-4 py-2 text-gray-700 font-medium">{row.brand}</td>
                        <td className="px-4 py-2 text-gray-500">{row.size}</td>
                        <td className="px-4 py-2 text-right font-mono text-gray-500">{row.system_filled}</td>
                        <td className="px-4 py-2 text-center">
                          <input type="number" min="0" value={row.ground_filled}
                            onChange={e => updateRow(row.brand, row.size, "ground_filled", e.target.value)}
                            className={`w-20 text-center border rounded-lg px-2 py-1 text-sm font-mono focus:outline-none ${hasDiff ? "border-amber-300 bg-amber-50" : "border-gray-300"}`}
                            data-testid={`gf-${row.brand}-${row.size}`} />
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-gray-500">{row.system_mt}</td>
                        <td className="px-4 py-2 text-center">
                          <input type="number" min="0" value={row.ground_mt}
                            onChange={e => updateRow(row.brand, row.size, "ground_mt", e.target.value)}
                            className={`w-20 text-center border rounded-lg px-2 py-1 text-sm font-mono focus:outline-none ${hasDiff ? "border-amber-300 bg-amber-50" : "border-gray-300"}`} />
                        </td>
                        <td className="px-4 py-2 text-center font-mono text-xs">
                          <span className={hasDiff ? "text-amber-700 font-bold" : "text-gray-300"}>
                            {df > 0 ? `+${df}` : df} / {dm > 0 ? `+${dm}` : dm}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)"
                className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
              <button onClick={saveTally} disabled={savingTally}
                className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-50"
                data-testid="save-tally-btn">
                {savingTally ? "Saving…" : "Save Tally"}
              </button>
              <button onClick={() => { setSuggestion(null); setTallyRows([]); }} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
            </div>
          </>
        )}
      </div>

      {/* Past tallies */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Past Tallies ({tallies.length})</span>
          <button onClick={load} className="p-1 hover:bg-gray-200 rounded"><RefreshCw size={12} /></button>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : tallies.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">No tallies yet. Start one above.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {tallies.map(t => (
              <div key={t.id} data-testid={`tally-${t.id}`}>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setExpanded(expanded === t.id ? null : t.id)} className="text-gray-400 hover:text-gray-600">
                      {expanded === t.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    <div>
                      <div className="font-semibold text-sm text-gray-800">{fmtDate(t.date)} — {t.godown}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {t.has_mismatch
                          ? <span className="text-amber-600"><AlertTriangle size={10} className="inline mr-1" />Mismatch: F{t.total_filled_diff > 0 ? "+" : ""}{t.total_filled_diff}, MT{t.total_mt_diff > 0 ? "+" : ""}{t.total_mt_diff}</span>
                          : <span className="text-green-600"><CheckCircle size={10} className="inline mr-1" />All matched</span>}
                        {t.note && <span className="ml-2 text-gray-400"> · {t.note}</span>}
                      </div>
                    </div>
                  </div>
                  {isAdmin && t.has_mismatch && !t.adjustment_id && (
                    <button onClick={() => applyAdj(t.id)} disabled={applyingAdj === t.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"
                      data-testid={`apply-adj-${t.id}`}>
                      {applyingAdj === t.id ? "Applying…" : "Apply Adjustment"}
                    </button>
                  )}
                  {t.adjustment_id && (
                    <span className="text-xs text-green-600 font-semibold">Adjustment applied</span>
                  )}
                </div>
                {expanded === t.id && (
                  <div className="px-12 pb-3">
                    <table className="w-full text-xs border border-gray-100 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-50">
                          {["Brand","Size","Sys F","Gnd F","Diff F","Sys MT","Gnd MT","Diff MT"].map(h => (
                            <th key={h} className="px-3 py-1.5 text-left text-gray-500 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(t.rows || []).map((r, i) => (
                          <tr key={i} className={`border-t border-gray-50 ${(r.diff_filled || r.diff_mt) ? "bg-amber-50/40" : ""}`}>
                            <td className="px-3 py-1.5 text-gray-700">{r.brand}</td>
                            <td className="px-3 py-1.5 text-gray-500">{r.size}</td>
                            <td className="px-3 py-1.5 font-mono">{r.system_filled}</td>
                            <td className="px-3 py-1.5 font-mono">{r.ground_filled}</td>
                            <td className={`px-3 py-1.5 font-mono font-bold ${r.diff_filled ? "text-amber-700" : "text-gray-300"}`}>{r.diff_filled > 0 ? `+${r.diff_filled}` : r.diff_filled}</td>
                            <td className="px-3 py-1.5 font-mono">{r.system_mt}</td>
                            <td className="px-3 py-1.5 font-mono">{r.ground_mt}</td>
                            <td className={`px-3 py-1.5 font-mono font-bold ${r.diff_mt ? "text-amber-700" : "text-gray-300"}`}>{r.diff_mt > 0 ? `+${r.diff_mt}` : r.diff_mt}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyTallyPage;
